import { useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { UserTable } from '@/components/users/user-table';
import { AddUserDialog } from '@/components/users/add-user-dialog';
import { PlusIcon } from 'lucide-react';
import { Toggle } from '@/components/ui/toggle';
import { PageHeader } from '@/components/shared/page-header';

export default function UsersPage() {
  const { t } = useTranslation();
  const [searchQuery, setSearchQuery] = useState('');
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [showPartiallyPaidOnly, setShowPartiallyPaidOnly] = useState(false);
  const [showStalePayments, setShowStalePayments] = useState(false);
  const userTableRef = useRef<{ fetchUsers: () => Promise<void> } | null>(null);

  const handleUserAdded = () => {
    // Refresh the user list
    if (userTableRef.current) {
      userTableRef.current.fetchUsers();
    }
  };

  return (
    <div className="space-y-6">
      <PageHeader title={t('users.title')}>
        <Input
          placeholder={t('users.search')}
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="w-full sm:w-64"
        />
        <div className="flex flex-wrap items-center gap-2">
          <Toggle
            pressed={showPartiallyPaidOnly}
            onPressedChange={setShowPartiallyPaidOnly}
            aria-label={t('users.filterPartiallyPaid')}
            className="whitespace-nowrap"
          >
            {t('users.partiallyPaidOnly', 'Partially Paid Only')}
          </Toggle>

          <Toggle
            pressed={showStalePayments}
            onPressedChange={setShowStalePayments}
            aria-label="Show stale payments"
            className="whitespace-nowrap"
          >
            Stale Payments
          </Toggle>

          <Button onClick={() => setShowAddDialog(true)}>
            <PlusIcon className="h-4 w-4 mr-2" />
            {t('users.addUser')}
          </Button>
        </div>
      </PageHeader>

      <Card>
        <CardHeader>
          <CardTitle>{t('users.usersList')}</CardTitle>
        </CardHeader>
        <CardContent>
          <UserTable
            searchQuery={searchQuery}
            showPartiallyPaidOnly={showPartiallyPaidOnly}
            showStalePayments={showStalePayments}
            ref={userTableRef}
          />
        </CardContent>
      </Card>

      <AddUserDialog
        open={showAddDialog}
        onOpenChange={setShowAddDialog}
        onSuccess={handleUserAdded}
      />
    </div>
  );
}
